import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from '@acme/ui';
import { AI_PROVIDERS, getModelConfig, getModelProvider, type ChatModel } from '@acme/util';
import { chatSessionsAddNewAtom, configuredProvidersAtom } from '../store/chat';
import { compareModelAtom } from '../store/compare';
import { apiKeyModalAtom, userAtom } from '../store/user';
import { useUserSubscription } from './useSubscription';

/** Only current registry models are accepted from a URL. */
const parseModel = (value: unknown): ChatModel | null => {
    if (typeof value !== 'string' || !value) return null;
    const config = getModelConfig(value);
    return config && !config.isDeprecated ? value : null;
};

const modelName = (id: ChatModel) => getModelConfig(id)?.displayName || id;

/**
 * Deep links from the marketing site into the app:
 *   /chat?model=<id>                 new chat on that model, compare off
 *   /chat?model=<a>&compare=<b>      new chat on <a>, compare mode against <b>
 *
 * Runs once, after the user is past the sign-in screen, then strips the
 * parameters so a reload or a shared URL doesn't create another chat. If a key
 * is missing it opens the key prompt for that provider, since that's the next
 * thing the visitor has to do.
 */
export const useModelDeepLink = (enabled: boolean) => {
    const router = useRouter();
    const user = useAtomValue(userAtom);
    const configured = useAtomValue(configuredProvidersAtom);
    const { isAllowAddChatSession } = useUserSubscription();
    const onNewChat = useSetAtom(chatSessionsAddNewAtom);
    const setCompareModel = useSetAtom(compareModelAtom);
    const setApiKeyModal = useSetAtom(apiKeyModalAtom);
    const handled = useRef(false);

    const userReady = !!(user?.id || user?.isLocal);

    useEffect(() => {
        if (!enabled || !router.isReady || handled.current || !userReady) return;
        const { model: rawModel, compare: rawCompare, ...rest } = router.query;
        if (rawModel === undefined && rawCompare === undefined) return;
        handled.current = true;

        const model = parseModel(rawModel);
        const compare = parseModel(rawCompare);
        if (!model) {
            void router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true });
            return;
        }

        const newId = onNewChat({ isAllowAddChatSession, defaultModel: model });
        setCompareModel(compare && compare !== model ? compare : null);
        const query = newId ? { ...rest, chatId: newId } : rest;
        void router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });

        const needed = [model, ...(compare && compare !== model ? [compare] : [])]
            .map(getModelProvider)
            .filter((p, i, all): p is NonNullable<typeof p> => !!p && all.indexOf(p) === i);
        const missing = needed.filter(p => !configured.includes(p));

        if (missing[0]) {
            toast(`Add your ${missing.map(p => AI_PROVIDERS[p].name).join(' and ')} API key${missing.length > 1 ? 's' : ''} to start.`);
            setApiKeyModal(missing[0]);
        } else if (compare && compare !== model) {
            toast.success(`Comparing ${modelName(model)} with ${modelName(compare)}. Send a message to see both answers.`);
        }
    }, [enabled, router, userReady, configured, isAllowAddChatSession, onNewChat, setCompareModel, setApiKeyModal]);
};
